import { useEffect } from 'react'

import type { UserHubEventListener, UserHubMessageType } from './user-hub.types'
import { useUserHub } from './user-hub-provider'

/**
 * Hook to subscribe to specific UserHub events
 *
 * @param eventType - The type of event to listen for
 * @param listener - Callback function that receives the event payload
 * @param enabled - Whether the subscription is active (default: true)
 *
 * @example
 * // Subscribe to temp sign mark uploads
 * useUserHubEvent('UserTempSignMarkUploaded', (payload) => {
 *   console.log('Sign mark uploaded:', payload.id)
 * })
 *
 * // Conditional subscription
 * useUserHubEvent('Notification', handleNotification, isEnabled)
 */
export function useUserHubEvent<T = unknown>(
  eventType: UserHubMessageType,
  listener: UserHubEventListener<T>,
  enabled: boolean = true,
): void {
  const { subscribe } = useUserHub()

  useEffect(() => {
    if (!enabled) {
      return
    }

    const unsubscribe = subscribe(eventType, listener)
    return unsubscribe
  }, [eventType, listener, enabled, subscribe])
}
